import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useBrand } from '@/brand/context'
import { useOnboarding } from '@/onboarding/context'
import { notConfigured } from '@/onboarding/steps'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'

/**
 * The reminder above every shell screen while setup is unfinished.
 *
 * S-D2's resume screen draws this banner on the dashboard, and it is the only
 * route back into the wizard once somebody has left it — without it, an
 * abandoned setup would be abandoned for good.
 *
 * **Four reasons it renders nothing**, and each is a real state rather than a
 * guard: the wizard has not loaded, there is nothing outstanding, the tenant
 * hid it during this sign-in, or the wizard has never been started at all.
 * The last one matters — a brand-new organisation is redirected into the wizard
 * by `Organisations`, and a banner telling them to finish something they have
 * not begun would be the shell nagging about its own redirect.
 *
 * **It comes back on every login** (owner decision 2026-09-15). *Hide for now*
 * — here, and *Hide until I next sign in* on the resume screen — posts the same
 * dismissal, and the server reports `dismissed_at` only to the session that
 * made it (`app/v1/onboarding._state`). A reminder that could be switched off
 * for ever was one that a tenant with a repository still unconnected never saw
 * again; one that could not be hidden at all sat on every screen of every
 * session. The button is here as well as on the resume screen because that
 * screen is two presses away, and the moment a tenant wants the banner gone is
 * the moment it is in front of them.
 *
 * Gated on `tenant:update`, which is what `POST /onboarding/dismiss` requires: a
 * member sees the banner — they need to know why the dashboard is thin — and
 * cannot hide it, exactly as they cannot finish the steps it names.
 */
export function SetupBanner(): JSX.Element | null {
  const brand = useBrand()
  const { state, adopt } = useOnboarding()
  const { membership } = useActiveTenant()
  const dismiss = useMutation(() => call(() => api.POST('/api/v1/onboarding/dismiss', {})))
  if (!state) return null
  if (state.dismissed_at) return null

  // "Not done", not `progress.outstanding`: a tenant who skipped three steps has
  // settled them as far as the wizard is concerned and has three things still
  // switched off, which is exactly what this banner is for (`notConfigured`).
  const remaining = notConfigured(state).length
  const configured = state.steps.length - remaining
  if (remaining === 0) return null
  if (configured === 0) return null

  return (
    <div className="ar-alert ar-alert--info app-setup-banner">
      <span className="ar-alert__icon" aria-hidden="true">
        ◷
      </span>
      {/* Parity P-SHELL-08 (2026-09-13). The shape `onboarding.html#resume`
          draws: one strong line — *"Setup is 3 of 6 done — pick up where you
          left off"* — over one sentence of body, and no button. The link is
          the strong line's second half, so the route back into the wizard is
          still one press. Kept in the shell as an app extra: the mockups draw
          the banner on the resume screen alone, but without it here an
          abandoned setup has no way back. */}
      <div className="ar-alert__body">
        <strong>
          Setup is {configured} of {state.steps.length} done —{' '}
          <Link to="/onboarding/resume">pick up where you left off</Link>
        </strong>
        {/* One expression: JSX drops the newline between `{brand.productName}`
            and the text after it, which printed "Limitedcan do" on the live
            tenant (2026-09-19). */}
        {`${remaining === 1 ? 'One step' : `${String(remaining)} steps`} left — some of what ${brand.productName} can do is still switched off.`}
      </div>
      {may(membership, 'tenant:update') ? (
        <Button
          tone="quiet"
          size="sm"
          busy={dismiss.pending}
          busyLabel="Hiding"
          title="Hidden until you next sign in"
          onClick={() => {
            void (async () => {
              const result = await dismiss.run(undefined)
              // Adopted rather than refetched, as every wizard write is: the
              // response is the whole state, with `dismissed_at` set for this
              // session, and the banner leaves on this render. A refused
              // dismissal leaves the banner where it is — there is nowhere on
              // an alert to print a second alert, and the resume screen's own
              // button reports the failure in full.
              if (result.ok) adopt(result.data)
            })()
          }}
        >
          Hide for now
        </Button>
      ) : null}
    </div>
  )
}
